import React, { useState } from 'react';
import { clientApi } from '../services/api';
import './CsvUpload.css';

interface CsvUploadProps {
  onSuccess: () => void;
}

const CsvUpload: React.FC<CsvUploadProps> = ({ onSuccess }) => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ success: number; errors: Array<{ row: number; error: string; data: any }>; total: number } | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
        setError('Por favor, selecciona un archivo CSV');
        return;
      }
      setFile(selectedFile);
      setError(null);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Por favor, selecciona un archivo CSV');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setResult(null);

      const fileContent = await file.text();
      const response = await clientApi.uploadCSV(fileContent);

      setResult(response.results);
      
      if (response.results.errors.length === 0) {
        alert(`✅ ${response.message}`);
        onSuccess();
        setFile(null);
        // Reset file input
        const fileInput = document.getElementById('csv-file-input') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
      } else {
        alert(`⚠️ ${response.message}\n\nRevisa los errores en la sección de resultados.`);
        onSuccess(); // Refresh anyway to show successful imports
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al subir el archivo CSV');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const template = `name,phone,email,city,license_plate,vehicle_type,is_teaching,motorcycle_brand,motorcycle_color,motorcycle_year,motorcycle_cc,motorcycle_model,motorcycle_features,rtm_date,birthday
Juan Pérez,3001234567,juan@example.com,La Dorada,ABC123,private,0,Yamaha,Azul,2020,150,MT-03,,2024-01-15,1990-05-20
María García,3007654321,maria@example.com,Manizales,XYZ789,public,1,Honda,Rojo,2019,200,CB200,,2024-02-20,1985-08-10`;

    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'plantilla_clientes.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="csv-upload-container">
      <div className="csv-upload-header">
        <h3>📤 Carga Masiva de Clientes</h3>
        <button onClick={downloadTemplate} className="btn-download-template">
          📥 Descargar Plantilla CSV
        </button>
      </div>

      <div className="csv-upload-info">
        <p><strong>Formato del CSV:</strong></p>
        <ul>
          <li><strong>Columnas requeridas:</strong> name, phone</li>
          <li><strong>Columnas opcionales:</strong> email, city, license_plate, vehicle_type, is_teaching, motorcycle_brand, motorcycle_color, motorcycle_year, motorcycle_cc, motorcycle_model, motorcycle_features, rtm_date, birthday</li>
          <li><strong>Formato de fechas:</strong> YYYY-MM-DD (ejemplo: 2024-01-15)</li>
          <li><strong>is_teaching:</strong> true/1 o false/0</li>
        </ul>
      </div>

      <div className="csv-upload-form">
        <input
          id="csv-file-input"
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          disabled={loading}
        />
        <button
          onClick={handleUpload}
          disabled={!file || loading}
          className="btn-upload-csv"
        >
          {loading ? 'Subiendo...' : 'Subir CSV'}
        </button>
      </div>

      {error && (
        <div className="csv-upload-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {result && (
        <div className="csv-upload-results">
          <h4>Resultados de la carga:</h4>
          <div className="results-summary">
            <p>✅ <strong>{result.success}</strong> clientes importados exitosamente</p>
            <p>❌ <strong>{result.errors.length}</strong> errores</p>
            <p>📊 Total procesado: <strong>{result.total}</strong> filas</p>
          </div>

          {result.errors.length > 0 && (
            <div className="errors-list">
              <h5>Errores encontrados:</h5>
              <table>
                <thead>
                  <tr>
                    <th>Fila</th>
                    <th>Error</th>
                    <th>Datos</th>
                  </tr>
                </thead>
                <tbody>
                  {result.errors.map((err, index) => (
                    <tr key={index}>
                      <td>{err.row}</td>
                      <td>{err.error}</td>
                      <td>{JSON.stringify(err.data)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CsvUpload;

